import React, { useEffect, useState } from "react";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import { BackArrowIcon, PenIcon, SaveCheckIcon, SaveCloseIcon, ShareIcon } from "../../assets/icons/icons";
import BtnLoad from "../../components/common/BtnLoad";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiclient from "../../apiclient";
import { PROJECT_DETAILS, PROJECT_NAME_CHANGE, PROJECT_PUBLISH, PROJECT_STATUS } from "../../apiclient/apiEndpoints";
import { CLONE, DELETE, SHARE, SHARE_EDIT } from "../../constant/constant";
import ShareModal from "../../components/modals/ShareModal";
import ShareEditModal from "../../components/modals/ShareEditModal";
import PublishProgress from "./PublishProgress";

const { BackButton, SimToggleSwitch, SimModal } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const defaultAction = {
  [SHARE]: false,
  [SHARE_EDIT]: false,
  [DELETE]: false,
  [CLONE]: false,
  loading: false,
  details: null,
};

// Notification component for error messages
function Notification({ message, onClose }) {
  return (
    <div className="custom-toast">
      <span style={{ flex: 1 }}>{message}</span>
      <button className="toast-close" onClick={onClose} title="Close">&times;</button>
    </div>
  );
}

const EditorHeader = ({ projectId, editorMode, setEditorMode, details, setDetails, getProjectDetails }) => {
  const navigate = useNavigate();
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  const [showEditName, setShowEditName] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [actionModal, setActionModal] = useState({ ...defaultAction });
  const [showPublish, setShowPublish] = useState(false);
  const [publishFail, setPublishFail] = useState(false);
  const [publishStatus, setPublishStatus] = useState("");
  const [notification, setNotification] = useState({ show: false, message: "" });

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  const handleSaveName = async () => {
    try {
      let bodyData = {
        name: details.name,
      };
      await apiclient.post(`${configData.config.API_URL}${PROJECT_NAME_CHANGE}/${projectId}`, bodyData);
    } catch (error) {
      console.log(error);
    } finally {
      handleCancel();
    }
  };

  const handleCancel = () => {
    getProjectDetails();
    setShowEditName(false);
  };

  const toggleEditor = (value) => {
    setEditorMode(value);
  };

  const handleNavigateBack = () => {
    navigate(`/project/${userData.user.uuid}`);
  };

  const handleOpenAction = (type, details) => {
    setActionModal({
      ...defaultAction,
      [type]: true,
      details,
    });
  };

  const handleCloseAction = () => {
    setActionModal({ ...defaultAction });
  };

  const handleHideProgressBar = () => {
    setShowPublish(false);
    setPublishStatus("");
  };

 
  const handleProjectPublish = async () => {
    if (publishLoading) return;
    setPublishFail(false);
    try {
      setPublishLoading(true);
      let data = await apiclient.get(`${configData.config.API_URL}${PROJECT_PUBLISH}/${projectId}`);
      const { versionId, preview, oldJSON, newJSON } = data;
      if (versionId) {
        navigate(`/${projectId}/${versionId}`);
      }
    } catch (error) {
      setPublishFail(true);
      const errorMsg = error?.response?.data?.message || "Publish failed!";
      setNotification({ show: true, message: errorMsg });
      console.log(errorMsg);
      console.log(error?.response?.data?.list || null);
    } finally {
      setPublishLoading(false);
      setTimeout(()=>{
        setPublishFail(false);
      }, [3000])
    }
  };

  return (
    <>
      {/* Notification for publish errors */}
      {notification.show && (
        <Notification
          message={notification.message}
          onClose={() => setNotification({ show: false, message: "" })}
        />
      )}
      {showPublish ? (
        <PublishProgress
          status={publishStatus}
          publishFail={publishFail}
          handleHideProgressBar={handleHideProgressBar}
        />
      ) : (
        <>
          <div className="editorHeader">
            <div className="logoTitle">
              <div className="py-1 h-100" onClick={handleNavigateBack}>
                <BackButton Icon={<BackArrowIcon />} />
              </div>

              <img src="https://cdn.simulanis.io/sso/logos/C.png" referrerPolicy="no-referrer" alt="Logo" />

              {!showEditName ? (
                <h1 title="name">{details.name}</h1>
              ) : (
                <input
                  className="title"
                  value={details.name}
                  onChange={(e) => setDetails((pre) => ({ ...pre, name: e.target.value }))}
                />
              )}

              {!showEditName ? (
                <span className="editBtn" onClick={() => setShowEditName(true)}>
                  <PenIcon />
                </span>
              ) : (
                <div className="d-flex align-items-center justify-content-start gap-2">
                  <span className="editBtn save" onClick={handleSaveName}>
                    <SaveCheckIcon />
                  </span>
                  <span className="editBtn" onClick={handleCancel}>
                    <SaveCloseIcon />
                  </span>
                </div>
              )}
            </div>

            <div className={`EditPreviewMode ${editorMode ? "edit" : "preview"}`}>
              <span className={`checkText ${editorMode ? "active" : ""}`} onClick={() => toggleEditor(true)}>
                Editor Mode
              </span>
              <span className={`checkText ${!editorMode ? "active" : ""}`} onClick={() => toggleEditor(false)}>
                Preview Mode
              </span>
            </div>
            <div className="headerActions">
              {publishFail ? (
                <button className="basicSave errorBtn"> Publish failed </button>
              ) : (
                <button className="save basicSave" onClick={handleProjectPublish}>
                  {publishLoading ? <BtnLoad /> : "Publish"}
                </button>
              )}

              <button className="basicSave share" onClick={() => handleOpenAction(SHARE, details)}>
                {" "}
                <ShareIcon />{" "}
              </button>
            </div>
          </div>

          <SimModal showModal={actionModal[SHARE]} onHide={handleCloseAction} centered size="md">
            <ShareModal
              details={actionModal?.details}
              onHide={handleCloseAction}
              onOpenEditModal={() => handleOpenAction(SHARE_EDIT, actionModal?.details)}
            />
          </SimModal>

          <SimModal showModal={actionModal[SHARE_EDIT]} onHide={handleCloseAction} centered size="md">
            <ShareEditModal
              onHide={handleCloseAction}
              details={actionModal?.details}
              handleOpenShareModal={() => handleOpenAction(SHARE, actionModal?.details)}
            />
          </SimModal>
        </>
      )}
    </>
  );
};

export default EditorHeader;
